import { BillingContact, BillingContactAddress, TaxId } from '@atlassian/cssxp-types-billing';
import isEmpty from 'lodash/isEmpty';
import negate from 'lodash/negate';
import * as React from 'react';
import Tooltip from '@atlaskit/tooltip';
import { TasstaxIdLabel } from '@css-xp/graphtypes/src/generated-types-new';

export interface Props {
  contact?: BillingContact;
  block: string;
}

const getCityStatePostalCode = ({ city, state, postalCode }: BillingContactAddress): string => {
  if (city && state) {
    return postalCode && !isEmpty(postalCode) ? `${city}, ${state} ${postalCode}` : `${city}, ${state}`;
  }

  return [city, state, postalCode].filter(negate(isEmpty)).join(' ');
};

const BillingShippingDocDetailBlockV2 = ({ contact, block }: Props) => {
  if (!contact) {
    return <div>{block === 'shipTo' ? 'Shipping ' : 'Billing '}information not found</div>;
  }

  const displayName = contact.fullName ?? 'No name';

  const hasFullAddress = contact.address.address1 && contact.address.country;

  const getTaxContent = (contact: BillingContact) => {
    const taxLabels = contact?.taxLabels;
    let taxIdLabels: TasstaxIdLabel[] | undefined;
    if (taxLabels && taxLabels.length > 0) {
      taxIdLabels = taxLabels[0]?.taxIdLabels as TasstaxIdLabel[];
    }
    const taxIds = contact?.taxIds;
    if (taxIds && taxIds.length > 0) {
      const sortedTaxDetails = taxIds
        .map((taxId: TaxId) => {
          const taxIdLabel = taxIdLabels?.filter(({ type }) => type && type === taxId?.label) ?? [];
          const displayTaxLabel = taxIdLabel.length > 0 ? taxIdLabel[0].displayName : '';
          return { ...taxId, displayTaxLabel: displayTaxLabel as string };
        })
        .sort((a, b) => (a.displayTaxLabel < b.displayTaxLabel ? -1 : 1));
      return (
        <div className="billing-shipping-detail-tax-id">
          {sortedTaxDetails.map((taxId, index) => (
            <div key={taxId?.id} data-testid={`taxId-${index}`}>{`${
              taxId.displayTaxLabel ? taxId.displayTaxLabel : ''
            } Tax ID: ${taxId?.id}`}</div>
          ))}
        </div>
      );
    }
    return <div className="billing-shipping-detail-tax-id">Tax ID: {contact?.taxId || 'Not found'}</div>;
  };

  return (
    <div className="billing-shipping-detail" data-testid={`billing-shipping-doc-${block}`}>
      {(block === 'billTo' || block === 'shipTo') && (
        <div
          className="billing-shipping-detail-name"
          style={{ color: '#0052cc' }}
          data-testid="billing-shipping-doc-name"
        >
          {displayName.length <= 15 ? (
            <strong>{displayName}</strong>
          ) : (
            <Tooltip content={displayName}>
              <strong>{displayName.slice(0, 12)}...</strong>
            </Tooltip>
          )}
        </div>
      )}
      {hasFullAddress ? (
        <>
          <div className="billing-shipping-detail-address">{contact.address.address1}</div>
          {contact.address.address2 && (
            <div className="billing-shipping-detail-address">{contact.address.address2}</div>
          )}
          <div className="billing-shipping-detail-city" data-testid="billing-shipping-detail-city-state">
            {getCityStatePostalCode(contact.address)}
          </div>

          <div className="billing-shipping-detail-country">{contact.address.country}</div>
          <div>{getTaxContent(contact)}</div>
        </>
      ) : (
        <div> Address: Not found</div>
      )}
    </div>
  );
};

export default BillingShippingDocDetailBlockV2;
